import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { loader, LoaderType } from '@app/decorators/decorators'
import { LoaderDirective } from '@app/dir/loader/loader.directive.js'
import { BaseModal } from '@app/pages/base.modal'
import { HardwareDevice } from '@app/reducers/hardwareDevice.reducer'
import { B1Service } from '@app/srv/b1.service'
import { BluetoothService } from '@app/srv/bluetooth.service'
import { HardwareDeviceService } from '@app/srv/hardwareDevice.service'
import { LogService } from '@app/srv/log.service'
import { Priority } from '@app/srv/popup.cnst'
import { PopupController } from '@app/srv/popup.controller'
import { QAService } from '@app/srv/qa.service'
import { dispatch, getState, select } from '@app/srv/store.service.js'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonItemGroup,
  IonLabel,
  IonList,
  IonListHeader,
  IonNote,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone'
import { AlertInput } from '@ionic/core'
import { _numberEnumKeys, localDate, localTime } from '@naturalcycles/js-lib'
import {
  BatteryStatus,
  FWVersion,
  FWVersionSeverity,
  HardwareId,
  LANG,
} from '@naturalcycles/shared'

@Component({
  selector: 'page-debug-b1',
  templateUrl: './debug-b1.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonIcon,
    IonTitle,
    IonContent,
    IonList,
    IonListHeader,
    IonItem,
    IonItemDivider,
    IonLabel,
    IonItemGroup,
    IonNote,
    LoaderDirective,
  ],
})
export class DebugB1Page extends BaseModal {
  className = 'DebugB1Page'

  protected override readonly blockPopups = false

  private b1Service = inject(B1Service)
  private bluetoothService = inject(BluetoothService)
  private hardwareDeviceService = inject(HardwareDeviceService)
  private logService = inject(LogService)
  private popupController = inject(PopupController)
  private qaService = inject(QAService)

  protected bluetoothEnabled = toSignal(this.bluetoothService.bluetoothEnabled$)
  protected connected = toSignal(this.bluetoothService.connected$)
  protected hardwareDevice = toSignal(this.hardwareDeviceService.hardwareDevice$)
  protected logs = this.logService.bleLogs

  protected bandRawData = toSignal(select(s => s.bandRawData))

  protected native = window.Capacitor.isNativePlatform()
  protected initialized = false

  protected readonly LANG = LANG

  constructor() {
    super()

    effect(() => {
      if (!this.connected()) return

      const { mac } = getState().hwDevice || {}
      if (!mac) return

      // TODO
      // void this.getDeviceStatus(mac)
    })
  }

  protected enableBluetooth(): void {
    void this.bluetoothService.enableBluetooth(true)
  }

  @loader(LoaderType.BUTTON)
  protected async scanAndPair(): Promise<void> {
    const initialized = await this.bluetoothService.initBluetooth('B1Debug')

    if (!initialized) return

    await this.bluetoothService.ensureBluetoothEnabled()

    const device = await this.bluetoothService.scanAndPair(HardwareId.B1)

    if (!device) return
    const serialNumber = await this.b1Service.readSerialNumber()

    const settings: HardwareDevice = {
      mac: device.deviceId,
      name: device.name,
      serialNumber,
      buzzer: false,
      backlight: false,
      testMeasurementDone: false,
      created: localTime.now().unix,
    }

    await this.hardwareDeviceService.saveHardwareDevice(settings, HardwareId.B1)
  }

  @loader(LoaderType.BUTTON)
  protected async fakePair(): Promise<void> {
    await this.qaService.pairFakeBand()
  }

  protected clearLogs(): void {
    this.logService.clearBleLogs()
  }

  protected async copyLogs(): Promise<void> {
    await this.logService.copyBleLogsToClipboard()
  }

  protected async unpair(mac: string): Promise<void> {
    await this.hardwareDeviceService.clearHardwareDevice(mac)
  }

  @loader(LoaderType.BUTTON)
  protected async setBatteryStatus(): Promise<void> {
    const inputs = _numberEnumKeys(BatteryStatus)
      .filter(label => label !== 'MEDIUM')
      .map(label => {
        return {
          type: 'radio',
          label,
          value: BatteryStatus[label],
        }
      }) as AlertInput[]

    await this.popupController.presentAlert(
      {
        header: 'Set battery status',
        inputs,
        buttons: [
          {
            text: 'Cancel',
            role: 'cancel',
            cssClass: 'secondary',
          },
          {
            text: 'Save',
            handler: value => {
              void this.hardwareDeviceService.saveBatteryStatus(value)
            },
          },
        ],
      },
      'alert-b1-battery',
      Priority.IMMEDIATE,
    )
  }

  protected async factoryReset(): Promise<void> {
    alert('Not implemented')
    // TODO
    // await this.b1Service.factoryReset(id)
  }

  @loader(LoaderType.BUTTON)
  protected async readAllMeasurements(): Promise<void> {
    await this.b1Service.readMeasurementData(true)
  }

  @loader(LoaderType.BUTTON)
  protected async readNewMeasurements(): Promise<void> {
    await this.b1Service.readMeasurementData()
  }

  protected clearAllMeasurements(): void {
    dispatch('clearBandRawData')
    alert('bandRawData state cleared')
  }

  @loader(LoaderType.BUTTON)
  protected async readSerialNumber(): Promise<void> {
    const serialNumber = await this.b1Service.readSerialNumber()
    alert(`Serial number: ${serialNumber}`)
  }

  @loader(LoaderType.BUTTON)
  protected async readDeviceStatus(): Promise<void> {
    const deviceStatus = await this.b1Service.readDeviceStatus(this.hardwareDevice()?.name)
    alert(`Device status: \n${JSON.stringify(deviceStatus)}`)
  }

  @loader(LoaderType.BUTTON)
  protected async readErrorStatus(): Promise<void> {
    const errorStatus = await this.b1Service.readErrorStatus(this.hardwareDevice()?.name)
    alert(`Error status: \n${JSON.stringify(errorStatus)}`)
  }

  @loader(LoaderType.BUTTON)
  protected async readDiagnostics(): Promise<void> {
    const diagnostics = await this.b1Service.readDiagnostics(this.hardwareDevice()?.name)
    alert(`Diagnostics: \n${JSON.stringify(diagnostics)}`)
  }

  @loader(LoaderType.BUTTON)
  protected async readFirmwareVersion(): Promise<void> {
    const fwVersion = await this.b1Service.readFirmwareVersion()
    alert(`Firmware version: ${fwVersion}`)
  }

  protected mockFirmwareVersion(): void {
    // TODO add actual fwVersion
    const fwVersion = prompt('Enter mock FW version (X.XX.X)', '4.12.1')
    dispatch('setHardwareDevice', { ...getState().hwDevice, fwVersion })
  }

  protected mockCriticalFWUpdate(): void {
    const releaseDate =
      prompt('Enter mock FW version release date (YYYY-mm-dd)') || localDate.todayString()
    // TODO add actual fwVersion
    dispatch('mockAvailableFWVersion', {
      version: '4.11.1',
      releaseDate,
      severity: FWVersionSeverity.CRITICAL,
    } as FWVersion)
    void this.dismissModal()
  }
}
